import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getWallet } from '../services/walletService';
import { useAuth } from './AuthContext';

/**
 * Holds the user's credit balance so every screen can show it without each
 * one fetching separately. Call refreshWallet() after anything that moves
 * credits (a top-up returning, paying with credits).
 */
const WalletContext = createContext();

export const WalletProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refreshWallet = useCallback(async () => {
    if (!isAuthenticated) {
      setWallet(null);
      return null;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getWallet();
      setWallet(data);
      return data;
    } catch (err) {
      // A missing balance shouldn't break the page it's displayed on.
      console.error('Failed to load wallet:', err);
      setError(err.message || 'Could not load your credit balance.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshWallet();
    } else {
      setWallet(null);
      setError(null);
    }
  }, [isAuthenticated, refreshWallet]);

  // Lets a caller apply a balance the API already returned, without a re-fetch.
  const setBalance = useCallback((balance) => {
    setWallet((prev) => (prev ? { ...prev, balance } : prev));
  }, []);

  const value = {
    wallet,
    balance: Number(wallet?.balance || 0),
    hasWallet: wallet !== null,
    loading,
    error,
    refreshWallet,
    setBalance,
  };

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};
